import { DateTime } from 'luxon';

export interface TimeSlotValidationInput {
  startTimeUTC: string;
  endTimeUTC: string;
  displayedStartTime?: string;
  patientTimezone: string;
  patientId: string;
}

export interface TimeSlotValidationResult {
  isValid: boolean;
  error?: Error;
  errorContext?: Record<string, unknown>;
  startTimeInPatientTZ?: DateTime;
  endTimeInPatientTZ?: DateTime;
  mismatchDetected?: boolean;
}

/**
 * Validates that appointment times are valid in the patient's timezone
 * and that displayed times match the converted times.
 */
export const validateTimeSlotInTimezone = (
  input: TimeSlotValidationInput,
): TimeSlotValidationResult => {
  const {
    startTimeUTC,
    endTimeUTC,
    displayedStartTime,
    patientTimezone,
    patientId,
  } = input;

  const startTimeInPatientTZ = DateTime.fromISO(startTimeUTC, {
    zone: 'utc',
  }).setZone(patientTimezone);

  const endTimeInPatientTZ = DateTime.fromISO(endTimeUTC, {
    zone: 'utc',
  }).setZone(patientTimezone);

  if (!startTimeInPatientTZ.isValid || !endTimeInPatientTZ.isValid) {
    const error = new Error('Invalid appointment time in patient timezone');
    const errorContext = {
      context: 'timezoneValidation.validateTimeSlot',
      patientId,
      patientTimezone,
      startTimeUTC,
      endTimeUTC,
      startTimeValid: startTimeInPatientTZ.isValid,
      endTimeValid: endTimeInPatientTZ.isValid,
      startTimeInvalidReason: startTimeInPatientTZ.invalidReason,
      endTimeInvalidReason: endTimeInPatientTZ.invalidReason,
    };

    return {
      isValid: false,
      error,
      errorContext,
    };
  }

  let mismatchDetected = false;
  if (displayedStartTime) {
    const startMeridiem = startTimeInPatientTZ.toFormat('a');
    const endMeridiem = endTimeInPatientTZ.toFormat('a');

    const formatTime = (dt: DateTime, includeMeridiem: boolean): string =>
      dt
        .toFormat(includeMeridiem ? 'h:mma' : 'h:mm')
        .toLowerCase()
        .replace(':00', '');

    const startFormatted = formatTime(
      startTimeInPatientTZ,
      startMeridiem !== endMeridiem,
    );
    const endFormatted = formatTime(endTimeInPatientTZ, true);
    const expectedDisplayString = `${startFormatted} - ${endFormatted}`;

    if (displayedStartTime !== expectedDisplayString) {
      mismatchDetected = true;
    }
  }

  return {
    isValid: true,
    startTimeInPatientTZ,
    endTimeInPatientTZ,
    mismatchDetected,
  };
};

/**
 * Logs timezone display mismatch to error tracking.
 */
export const logTimezoneMismatch = (
  logError: (error: Error, context: Record<string, unknown>) => void,
  params: {
    patientId: string;
    patientTimezone: string;
    displayedStartTime: string;
    expectedStartTime: string;
    startTimeUTC: string;
  },
): void => {
  logError(new Error('Timezone display mismatch detected'), {
    context: 'timezoneValidation.mismatch',
    patientId: params.patientId,
    patientTimezone: params.patientTimezone,
    displayedStartTime: params.displayedStartTime,
    expectedStartTime: params.expectedStartTime,
    startTimeUTC: params.startTimeUTC,
  });
};
